import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Scale, Loader2, Check, X, ArrowRight, RotateCcw, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CinematicVideo } from "@/components/CinematicVideo";

const BLUFF_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/rwsvao1h_gemini_generated_video_88717ef9.mp4";

const T = {
  fr: {
    eyebrow: "Le jeu de l'esprit", title: "Vrai ou Bluff",
    tagline: "Trois affirmations. Deux vraies, une fausse. Sauras-tu démasquer le bluff ?",
    topicPlaceholder: "Un sujet ? (optionnel)", play: "Jouer", surprise: "Surprends-moi",
    loading: "L'Élan prépare une manche…", instruction: "Clique sur l'affirmation qui te semble FAUSSE.",
    lie: "Bluff", truth: "Vrai", correct: "Bravo, tu as démasqué le bluff !", wrong: "Raté — celle-ci était vraie.",
    again: "Rejouer", error: "La manche n'a pas pu se créer. Réessaie.",
    invite: "Crée un compte pour explorer chaque réponse avec L'Élan.", inviteCta: "Entrer dans le Cocoon",
  },
  en: {
    eyebrow: "The mind game", title: "True or Bluff",
    tagline: "Three statements. Two true, one false. Can you unmask the bluff?",
    topicPlaceholder: "A topic? (optional)", play: "Play", surprise: "Surprise me",
    loading: "L'Élan is preparing a round…", instruction: "Click the statement you think is FALSE.",
    lie: "Bluff", truth: "True", correct: "Nailed it — you unmasked the bluff!", wrong: "Missed — that one was true.",
    again: "Play again", error: "The round couldn't be created. Try again.",
    invite: "Create an account to explore each answer with L'Élan.", inviteCta: "Enter the Cocoon",
  },
  es: {
    eyebrow: "El juego del ingenio", title: "Verdad o Farol",
    tagline: "Tres afirmaciones. Dos verdaderas, una falsa. ¿Sabrás desenmascarar el farol?",
    topicPlaceholder: "¿Un tema? (opcional)", play: "Jugar", surprise: "Sorpréndeme",
    loading: "L'Élan prepara una ronda…", instruction: "Haz clic en la afirmación que creas FALSA.",
    lie: "Farol", truth: "Verdad", correct: "¡Bien! Has desenmascarado el farol.", wrong: "Fallaste — esa era verdadera.",
    again: "Jugar otra vez", error: "No se pudo crear la ronda. Inténtalo de nuevo.",
    invite: "Crea una cuenta para explorar cada respuesta con L'Élan.", inviteCta: "Entrar en el Cocoon",
  },
  nl: {
    eyebrow: "Het geestesspel", title: "Waar of Bluf",
    tagline: "Drie beweringen. Twee waar, één onwaar. Ontmasker jij de bluf?",
    topicPlaceholder: "Een onderwerp? (optioneel)", play: "Spelen", surprise: "Verras me",
    loading: "L'Élan bereidt een ronde voor…", instruction: "Klik op de bewering die volgens jou ONWAAR is.",
    lie: "Bluf", truth: "Waar", correct: "Top — je hebt de bluf ontmaskerd!", wrong: "Mis — die was waar.",
    again: "Opnieuw spelen", error: "De ronde kon niet worden gemaakt. Probeer opnieuw.",
    invite: "Maak een account om elk antwoord met L'Élan te verkennen.", inviteCta: "Ga naar de Cocoon",
  },
};

export function VraiOuBluff() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const lang = (i18n.language || "fr").slice(0, 2);
  const c = T[lang] || T.fr;

  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(null);

  const play = async (surprise = false) => {
    if (loading) return;
    setLoading(true); setData(null); setPicked(null);
    try {
      const { data: res } = await api.get("/public/two-truths", {
        params: { lang, topic: surprise ? "" : topic.trim() },
      });
      setData(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || c.error);
    } finally { setLoading(false); }
  };

  const revealed = picked !== null;
  const pickedCorrect = revealed && data && data.statements[picked] && data.statements[picked].truth === false;

  return (
    <section className="relative px-6 my-20" data-testid="vraioubluff-section">
      <div className="max-w-4xl mx-auto">
        <div className="relative rounded-[2rem] overflow-hidden border border-primary/20 frame-pulse glass">
          <div aria-hidden className="absolute inset-0 pointer-events-none">
            <div className="absolute left-0 bottom-0 w-[380px] h-[380px] rounded-full bg-primary/10 blur-[120px]" />
          </div>
          <div className="relative z-10 px-6 sm:px-12 py-14 text-center">
            <div className="max-w-2xl mx-auto mb-10">
              <CinematicVideo fade noSound silver src={BLUFF_VIDEO} testId="vraioubluff-cinematic-video" caption={c.title} />
            </div>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/15 border border-primary/40 text-primary text-[11px] uppercase tracking-[0.28em] mb-6" data-testid="vraioubluff-eyebrow">
              <Scale size={13} /> {c.eyebrow}
            </span>
            <h2 className="font-display text-gradient-orange text-4xl sm:text-6xl leading-[1.12] pb-[0.08em] mb-4" data-testid="vraioubluff-title">{c.title}</h2>
            <p className="text-foreground/65 text-base sm:text-lg max-w-xl mx-auto leading-relaxed italic mb-9">{c.tagline}</p>

            {!data && (
              <div className="max-w-md mx-auto">
                <input
                  value={topic} onChange={(e) => setTopic(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && play(false)}
                  placeholder={c.topicPlaceholder}
                  data-testid="vraioubluff-topic-input"
                  className="w-full rounded-full bg-white/[0.04] border border-white/12 px-5 py-3 text-sm text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary/45 transition-colors mb-3"
                />
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <button onClick={() => play(false)} disabled={loading || !topic.trim()} data-testid="vraioubluff-play"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-primary/15 border border-primary/40 text-primary px-6 py-3 text-sm font-medium hover:bg-primary/25 transition-colors disabled:opacity-50">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} {c.play}
                  </button>
                  <button onClick={() => play(true)} disabled={loading} data-testid="vraioubluff-surprise"
                    className="btn-glass-soft flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm text-foreground/75 hover:text-primary disabled:opacity-50">
                    <Sparkles size={15} /> {c.surprise}
                  </button>
                </div>
              </div>
            )}

            {loading && !data && (
              <p className="text-center font-display italic text-foreground/40 flex items-center justify-center gap-2 py-6" data-testid="vraioubluff-loading">
                <Loader2 size={16} className="animate-spin" /> {c.loading}
              </p>
            )}

            {data && (
              <div className="max-w-xl mx-auto">
                {!revealed && <p className="text-center text-sm text-foreground/55 mb-4">{c.instruction}</p>}
                <div className="space-y-3">
                  {data.statements.map((s, i) => {
                    const isLie = s.truth === false;
                    let cls = "border-white/10 bg-white/[0.02] hover:border-primary/30";
                    if (revealed) {
                      cls = isLie ? "border-red-400/50 bg-red-400/[0.06]" : "border-emerald-400/30 bg-emerald-400/[0.04]";
                    } else if (picked === i) {
                      cls = "border-primary/50 bg-primary/[0.06]";
                    }
                    return (
                      <button
                        key={i}
                        onClick={() => !revealed && setPicked(i)}
                        disabled={revealed}
                        data-testid={`vraioubluff-statement-${i}`}
                        className={`w-full text-left rounded-2xl border p-4 transition-colors ${cls} ${revealed ? "cursor-default" : "cursor-pointer"}`}
                      >
                        <div className="flex items-start gap-3">
                          {revealed && (
                            <span className={`mt-0.5 flex-shrink-0 ${isLie ? "text-red-400" : "text-emerald-400"}`}>
                              {isLie ? <X size={18} /> : <Check size={18} />}
                            </span>
                          )}
                          <div>
                            <p className="text-foreground/90 leading-relaxed text-sm sm:text-base">{s.text}</p>
                            {revealed && (
                              <p className="mt-2 text-xs leading-relaxed text-foreground/55">
                                <span className={`font-semibold uppercase tracking-wide mr-1.5 ${isLie ? "text-red-400" : "text-emerald-400"}`}>
                                  {isLie ? c.lie : c.truth} ·
                                </span>
                                {s.note}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <AnimatePresence>
                  {revealed && (
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
                      <p className={`text-center font-display text-lg mb-5 ${pickedCorrect ? "text-emerald-400" : "text-red-400"}`} data-testid="vraioubluff-verdict">
                        {pickedCorrect ? c.correct : c.wrong}
                      </p>
                      <div className="flex flex-col items-center gap-4">
                        <button onClick={() => play(!topic.trim())} data-testid="vraioubluff-again"
                          className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
                          <RotateCcw size={15} /> {c.again}
                        </button>
                        {!user && (
                          <p className="text-sm text-foreground/50">
                            {c.invite}{" "}
                            <Link to="/entrer" data-testid="vraioubluff-signup" className="text-primary hover:underline font-medium">{c.inviteCta}</Link>
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
